import React from 'react';
import Modal from '../../../../components/Modal';
import { Discount } from '../../../../types/discounts';
import { Button, ButtonGroup } from '../../../../components/Buttons';
import { WithTranslation, withTranslation } from 'react-i18next';
import { getDiscountImage } from '../helpers';
import styled from 'styled-components/macro';
import Badges from './badges';
import Period from './period';
import { Tabs } from '../../../../components/Tabs';
import Item from './item';
import Stores from './stores';
import { getStoreIds } from '../../../../marketing/helpers';

interface DiscountDetailsProps extends WithTranslation {
    discount: Discount;
    clearActiveDiscount: () => void;
}

const DiscountDetails = (
    {
        clearActiveDiscount,
        discount,
        t,
    }: DiscountDetailsProps
) => {
    const image = getDiscountImage(discount);
    const [activeTab, setActiveTab] = React.useState('details');
    return (
        <Modal>
            <ModalInner>
                <Header>{discount.name || 'N/A'}</Header>

                <Tabs
                    groups={[
                        {
                            id: 'details',
                            option: t('customers.customer.discount.tabs.details'),
                            content: (
                                <ColContainer>
                                    <Left>
                                        <DiscountSection>
                                            <Badges discount={discount}/>
                                        </DiscountSection>

                                        {discount.description && (
                                            <DiscountSection>
                                                <SubHeader>{t('customers.customer.discount.description')}</SubHeader>
                                                {discount.description}
                                            </DiscountSection>
                                        )}

                                        <SectionHeader>{t('customers.customer.discount.requirements')}</SectionHeader>

                                        <DiscountSection>
                                            <Period discount={discount}/>
                                        </DiscountSection>

                                        {discount.requirement.item && (
                                            <DiscountSection>
                                                <Item discount={discount}/>
                                            </DiscountSection>
                                        )}

                                        {getStoreIds(discount) && (
                                            <DiscountSection>
                                                <Stores discount={discount}/>
                                            </DiscountSection>
                                        )}

                                    </Left>
                                    {image && (
                                        <Right>
                                            <Image src={image} alt={discount.name}/>
                                        </Right>
                                    )}
                                </ColContainer>
                            ),
                        },
                        {
                            id: 'json',
                            option: t('customers.customer.discount.tabs.json'),
                            content: (
                                <JSONData>{JSON.stringify(discount, null, 2)}</JSONData>
                            ),
                        },
                    ]}
                    onTabClick={(id: string) => setActiveTab(id)}
                    active={activeTab}
                />


                <br/>
                <br/>
                <ButtonGroup>
                    <Button
                        onClick={() => clearActiveDiscount()}
                    >
                        {t('customers.customer.discount.close')}
                    </Button>
                </ButtonGroup>
            </ModalInner>
        </Modal>
    );
};

const ModalInner = styled.div`
    min-width: 625px;
    @media (max-width: 830px) {
        min-width: auto;
    }
`;

const Header = styled.h1`
    font-size: 23px;
    color: #495E61;
    line-height: 32px;
`;

const SubHeader = styled.span`
    font-weight: 800;
    display: inline-block;
    margin-right: 8px;
`;

const SectionHeader = styled.h2`
    font-size: 14px;
    color: #7D8A8C;
    letter-spacing: 1px;
    text-transform: uppercase;
    margin-top: 20px;
`;

const ColContainer = styled.div`
    display: flex;
    margin-top: 20px;
    
    @media (max-width: 830px) {
        flex-direction: column-reverse;
    }
`;

const Left = styled.div`
    flex: 1 1 70%;
    
    @media (max-width: 830px) {
        flex: 1 1 100%;
    }
`;

const Right = styled.div`
    flex: 1 1 30%;
    padding-left: 20px;
    
    @media (max-width: 830px) {
        flex: 1 1 100%;
        margin-bottom: 20px;
        padding-left: 0;
        justify-content: center;
        display: flex;
    }
`;

const Image = styled.img`
    max-width: 200px;
    width: auto;
    height: auto;
`;

const DiscountSection = styled.div`
    & + & {
        margin-top: 16px;
    }
`;

const JSONData = styled.pre`
    background: #eee;
    padding: 10px;
    overflow-x: scroll;
`;

export default withTranslation()(DiscountDetails);
